import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, DeleteSweep as DeleteSweepIcon } from '@mui/icons-material';
import { videoApi, platformApi, adApi, videoViewStatisticsApi, backgroundProcessApi, publicationStatisticApi, dataPipelineApi } from '../services/api';
import { VideoD, PlatformD, AdD, VideoViewStatistics, BackgroundProcess, PublicationStatistic } from '../types';
import { useLocation } from 'react-router-dom';
import { formatAppDateTime } from '../utils/dateFormat';

function formatDateTime(val?: string, withTime: boolean = true): string {
  return formatAppDateTime(val, withTime, '', withTime);
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`data-tabpanel-${index}`}
      aria-labelledby={`data-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 1.25, sm: 1.5 } }}>{children}</Box>}
    </div>
  );
}

const DataView: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [videos, setVideos] = useState<VideoD[]>([]);
  const [platforms, setPlatforms] = useState<PlatformD[]>([]);
  const [ads, setAds] = useState<AdD[]>([]);
  const [statistics, setStatistics] = useState<VideoViewStatistics[]>([]);
  const [publicationStatistics, setPublicationStatistics] = useState<PublicationStatistic[]>([]);
  
  // Состояние для редактирования статистики
  const [editingStatistics, setEditingStatistics] = useState<VideoViewStatistics | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ views: 0, collectedTs: '' });
  const [clearing, setClearing] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const processId = params.get('processId');

  useEffect(() => {
    loadData();
    if (processId) {
      backgroundProcessApi.getById(Number(processId)).then(process => {
        if (["VIDEO_COLLECTION", "VK_VIDEO_SYNC", "YOUTUBE_SYNC", "COLLECT_ALL_VIDEO", "COLLECT_VIDEO_VK", "COLLECT_VIDEO_YOUTUBE", "COLLECT_VIDEO_RUTUBE"].includes(process.processCd)) {
          setTabValue(0); // Видео
        } else if (["STATS_COLLECTION", "COLLECT_VIEWS", "COLLECT_VIEWS_VK_VIDEO", "COLLECT_VIEWS_YOUTUBE", "COLLECT_VIEWS_RUTUBE"].includes(process.processCd)) {
          setTabValue(3); // Статистика
        } else if (["PUBLICATION_STATISTICS", "VKORD_SEND"].includes(process.processCd)) {
          setTabValue(4); // Публикация статистики
        } else {
          setTabValue(0);
        }
      });
    }
  }, [processId]);

  const loadData = async () => {
    try {
      setLoading(true);
      let videosRes, statsRes, platformsRes, adsRes, publicationStatisticsRes;
      if (processId) {
        [videosRes, platformsRes, adsRes, statsRes, publicationStatisticsRes] = await Promise.all([
          videoApi.getByProcess(Number(processId)),
          platformApi.getByProcess(Number(processId)),
          adApi.getByProcess(Number(processId)),
          videoViewStatisticsApi.getByProcess(Number(processId)),
          publicationStatisticApi.getByProcess(Number(processId))
        ]);
        setPlatforms(platformsRes);
        setAds(adsRes);
      } else {
        [videosRes, platformsRes, adsRes, statsRes, publicationStatisticsRes] = await Promise.all([
          videoApi.getAll(),
          platformApi.getAll(),
          adApi.getAll(),
          videoViewStatisticsApi.getAll(),
          publicationStatisticApi.getAll()
        ]);
        setPlatforms(platformsRes);
        setAds(adsRes);
      }
      setVideos(videosRes);
      setStatistics(statsRes);
      setPublicationStatistics(publicationStatisticsRes);
    } catch (err) {
      setError('Ошибка загрузки данных');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Функции для редактирования статистики
  const handleEditStatistics = (statistics: VideoViewStatistics) => {
    setEditingStatistics(statistics);
    setEditForm({
      views: statistics.views,
      collectedTs: formatDateTime(statistics.collectedTs)
    });
    setEditDialogOpen(true);
  };

  const handleSaveStatistics = async () => {
    if (!editingStatistics) return;
    
    try {
      const updatedStatistics = await videoViewStatisticsApi.update(
        editingStatistics.videoViewStatisticsId,
        {
          views: editForm.views,
          collectedTs: editForm.collectedTs
        }
      );
      
      // Обновляем данные в состоянии
      setStatistics(prev => 
        prev.map(stat => 
          stat.videoViewStatisticsId === editingStatistics.videoViewStatisticsId 
            ? updatedStatistics 
            : stat
        )
      );
      
      setEditDialogOpen(false);
      setEditingStatistics(null);
    } catch (err) {
      console.error('Error updating statistics:', err);
      setError('Ошибка обновления статистики');
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingStatistics(null);
  };

  const handleClearCollected = async () => {
    try {
      setClearing(true);
      setError(null);
      setSuccess(null);
      const r = await dataPipelineApi.clearCollected();
      setClearDialogOpen(false);
      setSuccess(
        `Очищено: видео ${r.videosDeleted ?? 0}, реклама ${r.adsDeleted ?? 0}, ` +
        `связки ${r.videoAdsDeleted ?? 0}, просмотры ${r.viewStatisticsDeleted ?? 0}, ` +
        `публикации ${r.publicationStatisticsDeleted ?? 0}. Кабинет ОРД не тронут.`
      );
      await loadData();
    } catch (err) {
      console.error(err);
      setError('Ошибка очистки данных');
    } finally {
      setClearing(false);
    }
  };

  const videoColumns: GridColDef[] = [
    { field: 'videoDId', headerName: 'ID', width: 80 },
    {
      field: 'url',
      headerName: 'Ссылка',
      width: 120,
      renderCell: (params) => params.value ? (
        <a href={params.value} target="_blank" rel="noopener noreferrer">Открыть</a>
      ) : null,
    },
    { field: 'title', headerName: 'Название', width: 300, flex: 1 },
    { field: 'videoExternalId', headerName: 'Video ID', width: 150 },
    { field: 'hostingCd', headerName: 'Хостинг', width: 120 },
    { field: 'createdTs', headerName: 'Дата создания', width: 150, renderCell: (params) => formatDateTime(params.value) },
    { field: 'publishedTs', headerName: 'Дата публикации', width: 150, renderCell: (params) => formatDateTime(params.value) },
  ];

  const platformColumns: GridColDef[] = [
    { field: 'platformDId', headerName: 'ID', width: 80 },
    { field: 'title', headerName: 'Название', width: 200, flex: 1 },
    { field: 'platformExternalId', headerName: 'Внешний ID', width: 150 },
    { field: 'createdTs', headerName: 'Создано', width: 150, renderCell: (params) => formatDateTime(params.value) },
  ];

  const adColumns: GridColDef[] = [
    { field: 'adDId', headerName: 'ID', width: 80 },
    { field: 'title', headerName: 'Название', width: 300, flex: 1 },
    { field: 'erid', headerName: 'ERID', width: 200 },
    { field: 'advertiserName', headerName: 'Рекламодатель', width: 200 },
    { field: 'createdTs', headerName: 'Дата создания', width: 150, renderCell: (params) => formatDateTime(params.value) },
  ];

  const statisticsColumns: GridColDef[] = [
    { field: 'videoViewStatisticsId', headerName: 'ID', width: 80 },
    { field: 'videoDId', headerName: 'Video ID', width: 100, type: 'number' },
    { field: 'videoTitle', headerName: 'Название видео', width: 200, flex: 1 },
    { field: 'videoExternalId', headerName: 'Внешний ID', width: 150 },
    { field: 'hostingCd', headerName: 'Хостинг', width: 120 },
    { field: 'views', headerName: 'Просмотры', width: 120, type: 'number' },
    { field: 'collectedTs', headerName: 'Дата сбора', width: 170, renderCell: (params) => formatDateTime(params.value) },
    {
      field: 'actions',
      headerName: 'Действия',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          size="small"
          onClick={() => handleEditStatistics(params.row)}
          title="Редактировать"
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  const publicationStatisticsColumns: GridColDef[] = [
    { field: 'publicationStatisticId', headerName: 'ID', width: 80 },
    { field: 'externalVideoId', headerName: 'Video ID', width: 150 },
    { field: 'erid', headerName: 'ERID', width: 120 },
    { field: 'platformExternalId', headerName: 'Platform External ID', width: 250 },
    { field: 'viewCount', headerName: 'Просмотры', width: 120 },
    { field: 'publishedTs', headerName: 'Дата публикации', width: 220, renderCell: (params) => formatDateTime(params.value) },
    { field: 'periodStartDt', headerName: 'Начало периода', width: 130, renderCell: (params) => formatDateTime(params.value, false) },
    { field: 'periodEndDt', headerName: 'Конец периода', width: 130, renderCell: (params) => formatDateTime(params.value, false) },
  ];

  if (loading && videos.length === 0 && !clearing) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={1}
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h5">Просмотр данных</Typography>
        <Button
          variant="outlined"
          color="error"
          startIcon={clearing ? <CircularProgress size={16} /> : <DeleteSweepIcon />}
          disabled={clearing}
          onClick={() => setClearDialogOpen(true)}
        >
          Очистить
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <CardContent sx={{ p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } } }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="data tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label={`Видео (${videos.length})`} />
              <Tab label={`Платформы (${platforms.length})`} />
              <Tab label={`Реклама (${ads.length})`} />
              <Tab label={`Статистика (${statistics.length})`} />
              <Tab label={`Публикация статистики (${publicationStatistics.length})`} />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <DataGrid
              rows={videos}
              columns={videoColumns}
              getRowId={(row) => row.videoDId}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 },
                },
                sorting: { sortModel: [{ field: 'publishedTs', sort: 'desc' }] },
              }}
              autoHeight
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <DataGrid
              rows={platforms}
              columns={platformColumns}
              getRowId={(row) => row.platformDId}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 },
                },
                sorting: { sortModel: [{ field: 'createdTs', sort: 'desc' }] },
              }}
              autoHeight
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <DataGrid
              rows={ads}
              columns={adColumns}
              getRowId={(row) => row.adDId}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 },
                },
                sorting: { sortModel: [{ field: 'createdTs', sort: 'desc' }] },
              }}
              autoHeight
            />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <DataGrid
              rows={statistics}
              columns={statisticsColumns}
              getRowId={(row) => row.videoViewStatisticsId}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 },
                },
                sorting: { sortModel: [{ field: 'collectedTs', sort: 'desc' }] },
              }}
              autoHeight
            />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <DataGrid
              rows={publicationStatistics}
              columns={publicationStatisticsColumns}
              getRowId={(row) => row.publicationStatisticId}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 },
                },
                sorting: { sortModel: [{ field: 'publishedTs', sort: 'desc' }] },
              }}
              autoHeight
            />
          </TabPanel>
        </CardContent>
      </Card>

      {/* Диалог редактирования статистики */}
      <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Редактировать статистику</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Просмотры"
              type="number"
              value={editForm.views}
              onChange={(e) => setEditForm(prev => ({ ...prev, views: parseInt(e.target.value) || 0 }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Дата сбора (DD.MM.YYYY HH:mm:ss)"
              value={editForm.collectedTs}
              onChange={(e) => setEditForm(prev => ({ ...prev, collectedTs: e.target.value }))}
              margin="normal"
              helperText="Формат: DD.MM.YYYY HH:mm:ss"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} startIcon={<CancelIcon />}>
            Отмена
          </Button>
          <Button onClick={handleSaveStatistics} variant="contained" startIcon={<SaveIcon />}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={clearDialogOpen} onClose={() => !clearing && setClearDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Очистить собранные данные?</DialogTitle>
        <DialogContent>
          Удалятся видео, реклама (erid), связки video↔erid, статистика просмотров и публикации.
          Также сбросится кэш сбора VK/YouTube.
          {' '}
          <strong>Кабинет ОРД</strong> (контрагенты, договоры, креативы) не тронется.
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button disabled={clearing} onClick={() => setClearDialogOpen(false)}>Отмена</Button>
          <Button
            color="error"
            variant="contained"
            disabled={clearing}
            onClick={handleClearCollected}
            startIcon={clearing ? <CircularProgress size={16} color="inherit" /> : <DeleteSweepIcon />}
          >
            Очистить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataView; 